import { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { KPICard } from '../components/KPICard';
import { getFundedDeals, toggleDealFunding, archiveDeal, bulkArchiveDeals } from '../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, Eye, Loader2, Download, Search, Clock,
  TrendingUp, CheckCircle, BarChart3, Undo2, Calendar, Archive,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (val) => {
  const num = Number(val) || 0;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const toDateStr = (d) => d.toISOString().split('T')[0];

const getPresetRange = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case 'today':
      return { from: toDateStr(today), to: toDateStr(today), label: 'Today' };
    case 'yesterday': {
      const yd = new Date(today);
      yd.setDate(yd.getDate() - 1);
      return { from: toDateStr(yd), to: toDateStr(yd), label: 'Yesterday' };
    }
    case '7d': {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      return { from: toDateStr(from), to: toDateStr(today), label: 'Last 7 Days' };
    }
    case '30d': {
      const from = new Date(today);
      from.setDate(from.getDate() - 29);
      return { from: toDateStr(from), to: toDateStr(today), label: 'Last 30 Days' };
    }
    case 'mtd': {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: toDateStr(from), to: toDateStr(today), label: 'Month to Date' };
    }
    case 'all':
      return { from: '', to: '', label: 'All Time' };
    default:
      return { from: '', to: '', label: 'All Time' };
  }
};

export const FundedDealsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ deals: [], stats: {}, charts: {}, filters: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [manager, setManager] = useState('');
  const [lender, setLender] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activePreset, setActivePreset] = useState('all');
  const [sortField, setSortField] = useState('funded_at');
  const [sortDir, setSortDir] = useState('desc');
  const [unfunding, setUnfunding] = useState(null);
  const [archiveModal, setArchiveModal] = useState({ open: false, dealId: null, dealNumber: null });
  const [archiving, setArchiving] = useState(false);
  const { user } = useAuth();
  const canBulkArchive = ['admin', 'general_manager', 'finance_manager'].includes(user?.role);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkArchiving, setBulkArchiving] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (manager) params.manager = manager;
      if (lender) params.lender = lender;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await getFundedDeals(params);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load funded deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [manager, lender, dateFrom, dateTo]);

  const applyPreset = (preset) => {
    const range = getPresetRange(preset);
    setActivePreset(preset);
    setDateFrom(range.from);
    setDateTo(range.to);
  };

  const handleDateFromChange = (val) => {
    setDateFrom(val);
    setActivePreset('custom');
    if (dateTo && val > dateTo) setDateTo(val);
  };

  const handleDateToChange = (val) => {
    setDateTo(val);
    setActivePreset('custom');
    if (dateFrom && val < dateFrom) setDateFrom(val);
  };

  const handleUnfund = async (dealId, dealNumber) => {
    setUnfunding(dealId);
    try {
      await toggleDealFunding(dealId, false);
      toast.success(`Deal #${dealNumber} moved back to active`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unfund deal');
    } finally {
      setUnfunding(null);
    }
  };

  const openArchiveConfirm = (dealId, dealNumber) => {
    setArchiveModal({ open: true, dealId, dealNumber });
  };

  const closeArchiveConfirm = () => {
    if (archiving) return;
    setArchiveModal({ open: false, dealId: null, dealNumber: null });
  };

  const handleConfirmArchive = async () => {
    const { dealId, dealNumber } = archiveModal;
    if (!dealId) return;
    setArchiving(true);
    try {
      await archiveDeal(dealId);
      toast.success(`Deal #${dealNumber} archived`);
      setArchiveModal({ open: false, dealId: null, dealNumber: null });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive deal');
    } finally {
      setArchiving(false);
    }
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    if (manager) params.set('manager', manager);
    if (lender) params.set('lender', lender);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    window.open(`${API_URL}/api/deals/funded/export?${params.toString()}`, '_blank');
  };

  const sortedDeals = [...(data.deals || [])].filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return d.customer_name?.toLowerCase().includes(q) ||
      d.deal_number?.toLowerCase().includes(q) ||
      d.vehicle_info?.toLowerCase().includes(q) ||
      d.lender?.toLowerCase().includes(q);
  }).sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortField === 'amount_financed') return (a.amount_financed - b.amount_financed) * dir;
    if (sortField === 'days_to_fund') return (a.days_to_fund - b.days_to_fund) * dir;
    const aVal = a[sortField] || '';
    const bVal = b[sortField] || '';
    return aVal > bVal ? dir : aVal < bVal ? -dir : 0;
  });

  // Bulk selection derived state
  const visibleIds = useMemo(() => sortedDeals.map(d => d.id), [sortedDeals]);
  const selectedVisibleIds = useMemo(() => visibleIds.filter(id => selectedIds.has(id)), [visibleIds, selectedIds]);
  const selectedCount = selectedVisibleIds.length;
  const masterState = selectedCount === 0
    ? false
    : (selectedCount === visibleIds.length ? true : 'indeterminate');

  // Clear selection whenever the effective result set changes (filters/search/manager/lender/date/preset)
  useEffect(() => {
    setSelectedIds(new Set());
  }, [search, manager, lender, dateFrom, dateTo, activePreset]);

  // Prune selection if the fetched dataset no longer contains ids (e.g. after archive)
  useEffect(() => {
    setSelectedIds(prev => {
      const visible = new Set(visibleIds);
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        if (visible.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [visibleIds]);

  const toggleRow = (dealId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(dealId)) next.delete(dealId);
      else next.add(dealId);
      return next;
    });
  };

  const toggleMaster = () => {
    if (masterState === true) {
      // Currently all selected -> clear
      setSelectedIds(new Set());
    } else {
      // None or partial -> select all visible
      setSelectedIds(new Set(visibleIds));
    }
  };

  const handleConfirmBulkArchive = async () => {
    if (selectedCount === 0) return;
    const ids = [...selectedVisibleIds];
    setBulkArchiving(true);
    try {
      const res = await bulkArchiveDeals(ids);
      const { archived_count = 0, failed_count = 0, failed = [] } = res.data || {};
      if (archived_count > 0 && failed_count === 0) {
        toast.success(`${archived_count} deal${archived_count === 1 ? '' : 's'} archived successfully`);
      } else if (archived_count > 0 && failed_count > 0) {
        const failedNums = failed.map(f => f.deal_number || f.deal_id).slice(0, 3).join(', ');
        const more = failed.length > 3 ? ` (+${failed.length - 3} more)` : '';
        toast.warning(`${archived_count} archived, ${failed_count} failed: ${failedNums}${more}`);
      } else {
        toast.error(`No deals archived. ${failed_count} failed.`);
      }
      setBulkModalOpen(false);
      setSelectedIds(new Set());
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk archive failed');
    } finally {
      setBulkArchiving(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const stats = data.stats || {};
  const charts = data.charts || {};
  const filtersData = data.filters || {};

  const presets = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: '7d', label: '7 Days' },
    { key: '30d', label: '30 Days' },
    { key: 'mtd', label: 'MTD' },
    { key: 'all', label: 'All Time' },
  ];

  const activeLabel = activePreset === 'custom'
    ? `${dateFrom || '...'} — ${dateTo || '...'}`
    : presets.find(p => p.key === activePreset)?.label || 'All Time';

  return (
    <Layout>
      <Header
        title="Funded Deals"
        subtitle={`${sortedDeals.length} in queue · ${stats.total_deals_funded || 0} funded all-time${activePreset !== 'all' ? ` · ${activeLabel}` : ''}`}
      />

      <div className="p-6 space-y-6">
        {/* Date Range Filter */}
        <div className="bg-white border border-slate-200 p-4" data-testid="date-range-section">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <Calendar size={14} /> Period
            </div>
            <div className="flex items-center gap-1 bg-slate-100 rounded p-0.5">
              {presets.map(p => (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p.key)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                    activePreset === p.key
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid={`preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => handleDateFromChange(e.target.value)}
                className="border border-slate-200 rounded px-2 py-1.5 text-sm"
                data-testid="date-from"
              />
              <span className="text-slate-400 text-sm">to</span>
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => handleDateToChange(e.target.value)}
                className="border border-slate-200 rounded px-2 py-1.5 text-sm"
                data-testid="date-to"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="bg-white border border-slate-200 p-8 text-center">
            <Loader2 size={32} className="mx-auto animate-spin text-slate-400 mb-2" />
            <p className="text-slate-500">Loading funded deals...</p>
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="funded-kpis">
              <KPICard
                label="Total Funded Amount"
                value={formatCurrency(stats.total_funded_amount)}
                icon={DollarSign}
                type="success"
              />
              <KPICard
                label="Deals Funded"
                value={stats.total_deals_funded || 0}
                icon={CheckCircle}
                type="default"
              />
              <KPICard
                label="Avg. Days to Fund"
                value={`${stats.avg_days_to_fund || 0} days`}
                icon={Clock}
                type={stats.avg_days_to_fund > 5 ? 'urgent' : 'default'}
              />
            </div>

            {/* Charts Row */}
            {(charts.by_lender?.length > 0 || charts.by_manager?.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* By Lender */}
                {charts.by_lender?.length > 0 && (
                  <div className="bg-white border border-slate-200 p-5" data-testid="chart-by-lender">
                    <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3 flex items-center gap-2">
                      <BarChart3 size={14} /> Funding by Lender
                    </h3>
                    <div className="space-y-2">
                      {charts.by_lender.map((item) => {
                        const pct = stats.total_funded_amount > 0 ? (item.amount / stats.total_funded_amount) * 100 : 0;
                        return (
                          <div key={item.name}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-medium text-slate-700 truncate">{item.name}</span>
                              <span className="text-slate-500 font-mono">{formatCurrency(item.amount)}</span>
                            </div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* By Manager */}
                {charts.by_manager?.length > 0 && (
                  <div className="bg-white border border-slate-200 p-5" data-testid="chart-by-manager">
                    <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3 flex items-center gap-2">
                      <BarChart3 size={14} /> Funding by Manager
                    </h3>
                    <div className="space-y-2">
                      {charts.by_manager.map((item) => {
                        const pct = stats.total_funded_amount > 0 ? (item.amount / stats.total_funded_amount) * 100 : 0;
                        return (
                          <div key={item.name}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-medium text-slate-700 truncate">{item.name}</span>
                              <span className="text-slate-500 font-mono">{formatCurrency(item.amount)}</span>
                            </div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Filters + Search + Export */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search customer, deal #, vehicle..."
                  className="pl-9"
                  data-testid="funded-search"
                />
              </div>
              <select
                value={manager}
                onChange={(e) => setManager(e.target.value)}
                className="border border-slate-200 rounded px-3 py-2 text-sm"
                data-testid="funded-filter-manager"
              >
                <option value="">All Managers</option>
                {filtersData.managers?.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select
                value={lender}
                onChange={(e) => setLender(e.target.value)}
                className="border border-slate-200 rounded px-3 py-2 text-sm"
                data-testid="funded-filter-lender"
              >
                <option value="">All Lenders</option>
                {filtersData.lenders?.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-funded-csv">
                <Download size={14} className="mr-1" /> Export CSV
              </Button>
            </div>

            {/* Table */}
            {sortedDeals.length === 0 ? (
              <div className="bg-white border border-slate-200 p-12 text-center">
                <CheckCircle size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-600 mb-2">No Funded Deals</h3>
                <p className="text-slate-500 mb-4">
                  Deals marked as funded will appear here with full tracking and reporting.
                </p>
                <Button variant="outline" onClick={() => navigate('/')}>
                  Back to Dashboard
                </Button>
              </div>
            ) : (
              <div className="bg-white border border-slate-200">
                {canBulkArchive && selectedCount > 0 && (
                  <div
                    className="flex items-center justify-between gap-3 px-4 py-3 bg-blue-50 border-b border-blue-200"
                    data-testid="bulk-action-bar"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-slate-800" data-testid="bulk-selected-count">
                        {selectedCount} deal{selectedCount === 1 ? '' : 's'} selected
                      </span>
                      <button
                        type="button"
                        onClick={() => setSelectedIds(new Set())}
                        className="text-xs text-slate-500 hover:text-slate-700 underline"
                        data-testid="bulk-clear-selection"
                      >
                        Clear
                      </button>
                    </div>
                    <Button
                      onClick={() => setBulkModalOpen(true)}
                      disabled={bulkArchiving}
                      className="bg-slate-800 hover:bg-slate-900 text-white"
                      data-testid="bulk-archive-btn"
                    >
                      <Archive size={14} className="mr-1" />
                      Archive Selected
                    </Button>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="funded-deals-table">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        {canBulkArchive && (
                          <th className="w-10 px-4 py-3 text-left">
                            <Checkbox
                              checked={masterState}
                              onCheckedChange={toggleMaster}
                              disabled={visibleIds.length === 0}
                              aria-label="Select all deals on this page"
                              data-testid="bulk-select-all-checkbox"
                            />
                          </th>
                        )}
                        {[
                          { key: 'deal_number', label: 'Deal #' },
                          { key: 'customer_name', label: 'Customer' },
                          { key: 'vehicle_info', label: 'Vehicle' },
                          { key: 'lender', label: 'Lender' },
                          { key: 'finance_manager', label: 'Manager' },
                          { key: 'amount_financed', label: 'Amount Funded' },
                          { key: 'funded_at', label: 'Funded Date' },
                          { key: 'days_to_fund', label: 'Days to Fund' },
                          { key: 'risk_level', label: 'Risk' },
                        ].map(col => (
                          <th
                            key={col.key}
                            onClick={() => handleSort(col.key)}
                            className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 cursor-pointer hover:text-slate-700 select-none"
                          >
                            {col.label}<SortIcon field={col.key} />
                          </th>
                        ))}
                        <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDeals.map((deal) => (
                        <tr key={deal.id} className={`border-b border-slate-100 hover:bg-slate-50 ${selectedIds.has(deal.id) ? 'bg-blue-50/40' : ''}`}>
                          {canBulkArchive && (
                            <td className="w-10 px-4 py-3">
                              <Checkbox
                                checked={selectedIds.has(deal.id)}
                                onCheckedChange={() => toggleRow(deal.id)}
                                aria-label={`Select deal ${deal.deal_number}`}
                                data-testid={`bulk-select-${deal.id}`}
                              />
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <span className="font-mono font-semibold text-slate-900">{deal.deal_number}</span>
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-900">{deal.customer_name}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{deal.vehicle_info}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{deal.lender}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{deal.finance_manager}</td>
                          <td className="px-4 py-3">
                            <span className="font-semibold text-emerald-700">{formatCurrency(deal.amount_financed)}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {deal.funded_at ? new Date(deal.funded_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`font-mono text-sm ${deal.days_to_fund > 5 ? 'text-red-600 font-semibold' : deal.days_to_fund > 3 ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {deal.days_to_fund}d
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${
                              deal.risk_level === 'red' ? 'bg-red-100 text-red-700' :
                              deal.risk_level === 'yellow' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                            }`}>
                              {deal.risk_level?.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/deal/${deal.id}`)}
                                data-testid={`view-funded-${deal.id}`}
                              >
                                <Eye size={14} className="mr-1" /> View
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUnfund(deal.id, deal.deal_number)}
                                disabled={unfunding === deal.id}
                                className="text-amber-600 hover:text-amber-700"
                                data-testid={`unfund-${deal.id}`}
                              >
                                {unfunding === deal.id ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <><Undo2 size={14} className="mr-1" /> Unfund</>
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openArchiveConfirm(deal.id, deal.deal_number)}
                                className="text-slate-600 hover:text-slate-800"
                                data-testid={`archive-funded-${deal.id}`}
                              >
                                <Archive size={14} className="mr-1" /> Archive
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Archive Confirmation Modal */}
      {archiveModal.open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={closeArchiveConfirm}
          data-testid="archive-modal-backdrop"
        >
          <div
            className="bg-white w-full max-w-lg border border-slate-200 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="archive-modal"
          >
            <div className="p-6 border-b border-slate-200 flex items-start gap-3">
              <Archive size={22} className="text-slate-600 mt-0.5" />
              <div>
                <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-900">
                  Archive this funded deal?
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  Archiving will remove deal #{archiveModal.dealNumber} from the Funded Deals queue.
                  The deal and its history will remain available in Archived Deals, and it will
                  continue to count toward historical funding totals.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3">
              <Button
                variant="ghost"
                onClick={closeArchiveConfirm}
                disabled={archiving}
                data-testid="archive-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmArchive}
                disabled={archiving}
                className="bg-slate-800 hover:bg-slate-900 text-white"
                data-testid="archive-confirm-btn"
              >
                {archiving ? (
                  <><Loader2 size={14} className="mr-1 animate-spin" /> Archiving...</>
                ) : (
                  <><Archive size={14} className="mr-1" /> Archive Deal</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Bulk Archive Confirmation Modal */}
      {bulkModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => !bulkArchiving && setBulkModalOpen(false)}
          data-testid="bulk-archive-modal-backdrop"
        >
          <div
            className="bg-white w-full max-w-lg border border-slate-200 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="bulk-archive-modal"
          >
            <div className="p-6 border-b border-slate-200 flex items-start gap-3">
              <Archive size={22} className="text-slate-600 mt-0.5" />
              <div>
                <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-900" data-testid="bulk-archive-modal-title">
                  Archive {selectedCount} funded deal{selectedCount === 1 ? '' : 's'}?
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  These deals will be removed from the active Funded Deals queue and moved to
                  Archived Deals. Their deal history and funding records will be preserved,
                  and they will continue to count toward historical funding totals.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => setBulkModalOpen(false)}
                disabled={bulkArchiving}
                data-testid="bulk-archive-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmBulkArchive}
                disabled={bulkArchiving || selectedCount === 0}
                className="bg-slate-800 hover:bg-slate-900 text-white"
                data-testid="bulk-archive-confirm-btn"
              >
                {bulkArchiving ? (
                  <><Loader2 size={14} className="mr-1 animate-spin" /> Archiving {selectedCount} deals...</>
                ) : (
                  <><Archive size={14} className="mr-1" /> Archive Deals</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
